"use client";

import { useEffect, useState, useMemo, useCallback, memo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";

import {
  fetchServiceOfDay,
  fetchCategoriesOption,
} from "../config/redux/slices/categoriesSlice";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { X, CheckCircle, XCircle, Star, Gift, Zap, Shield, UserCheck, DollarSign, Headphones, Search, Calendar, ShoppingCart, Clock, Sparkles, BadgeCheck, MapPin, CalendarClock, CreditCard, Smile, Plus, Minus, Lightbulb } from "lucide-react";
import { addToCart, increaseQuantity, decreaseQuantity } from "../config/redux/slices/cartSlice";

import CategoryIcon from "./CategoryIcon";
import SubcategoryIcon from "./SubcategoryIcon";
import AddToCartButton from "./AddToCart";
import AddToCartBannerButton from "./AddtoCartBanner";
import Banner from "./Banner";
import DeepCleaning from "./DeepCleaning";
import ServiceOfDayBar from "./ServiceOfDayBar";
import InfiniteMotionTestimonials from "./InfiniteMotionTestimonials";
import HowSortkaroWorks from "./HowSortkaroWorks";
import WhyChooseSortKaro from "./WhyChooseSortKaro";
// import UpcomingBookingsOverlay from "./UpcomingBookingsOverlay";
// import MobileUpcomingBookingsCard from "./MobileUpcomingBookingsCard";
import FullBadge from "./FullBadge";

/* ================================ */
/*          STATIC DATA              */
/* ================================ */

const trustItems = [
  { icon: BadgeCheck, label: "Verified Professionals", badge: "100%", badgeColor: "bg-blue-100 text-blue-700", description: "All experts are verified & trained" },
  { icon: UserCheck, label: "Background Verified", badge: "Safe", badgeColor: "bg-green-100 text-green-700", description: "Police verification completed" },
  { icon: DollarSign, label: "Best Price Guarantee", badge: "₹", badgeColor: "bg-blue-100 text-blue-700", description: "Transparent & competitive pricing" },
  { icon: Shield, label: "Secure & Insured", badge: "24/7", badgeColor: "bg-orange-100 text-orange-700", description: "Your safety is our priority" },
];

const steps = [
  { icon: CheckCircle, label: "Select", sublabel: "Service" },
  { icon: MapPin, label: "Address", sublabel: "Location" },
  { icon: CalendarClock, label: "Schedule", sublabel: "Time" },
  { icon: CreditCard, label: "Payment", sublabel: "Secure" },
  { icon: Smile, label: "Relax", sublabel: "Enjoy" },
];

const reviewsPreview = [
  {
    name: "Priya Sharma",
    initials: "PS",
    location: "Sector 56, Gurugram",
    daysAgo: "5 days ago",
    rating: 5,
    text: "Very satisfied with the quality of work. They were thorough and used eco-friendly products.",
    service: "Sweeping and Mopping Cleaning"
  },
  {
    name: "Amit Kumar",
    initials: "AK",
    location: "DLF Phase 2, Gurugram",
    daysAgo: "3 days ago",
    rating: 5,
    text: "Great service, on time and professional! Highly recommend SortKaro.",
    service: "Deep Cleaning"
  },
  {
    name: "Neha Gupta",
    initials: "NG",
    location: "Sector 45, Gurugram",
    daysAgo: "1 week ago",
    rating: 5,
    text: "Amazing experience! The team was punctual and did an excellent job.",
    service: "Kitchen Cleaning"
  },
  {
    name: "Rahul Verma",
    initials: "RV",
    location: "Sector 82, Gurugram",
    daysAgo: "2 days ago",
    rating: 5,
    text: "Excellent deep cleaning service. My house looks brand new! Will definitely use again.",
    service: "Full House Deep Cleaning"
  },
  {
    name: "Anjali Singh",
    initials: "AS",
    location: "Sohna Road, Gurugram",
    daysAgo: "4 days ago",
    rating: 5,
    text: "Very professional team. They cleaned every corner of my bathroom perfectly.",
    service: "Bathroom Deep Cleaning"
  },
  {
    name: "Vikram Patel",
    initials: "VP",
    location: "Golf Course Road",
    daysAgo: "1 day ago",
    rating: 5,
    text: "Best home service I've ever used. Fast booking and excellent work quality!",
    service: "Sofa Cleaning"
  },
];

/* ================================ */
/*          HELPER FUNCTIONS         */
/* ================================ */
const calculateDiscountedPrice = (originalPrice, discountValue, discountUnit) => {
  if (discountValue < 0) return originalPrice;

  let discountAmount = 0;
  if (discountUnit === "PERCENTAGE") {
    if (discountValue > 100) return originalPrice;
    discountAmount = (originalPrice * discountValue) / 100;
  } else if (discountUnit === "FLAT") {
    if (discountValue > originalPrice) return originalPrice;
    discountAmount = discountValue;
  } else return originalPrice;

  return originalPrice - discountAmount;
};

const CAR_WASH_LIMITS = {
  "Hatchback / Sedan": 3,
  "SUV / MUV": 3,
  "Luxury Car": 3,
  "Truck / Bus": 3,
  "Bike / Scooter": 5,
};

const getMaxQtyForOption = (name) => CAR_WASH_LIMITS[name] ?? Number.POSITIVE_INFINITY;

const getMaxQtyMessage = (name) => {
  if (name === "Bike / Scooter") return "Maximum quantity of 5 reached for Bike / Scooter.";
  if (CAR_WASH_LIMITS[name] === 3) return "Maximum quantity of 3 reached for this item.";
  return "Maximum quantity reached for this item.";
};

/* ================================ */
/*          SKELETONS                */
/* ================================ */
const ServiceOfDayBarSkeleton = () => <div className="w-full max-w-[1200px] mx-auto bg-gray-200 rounded-[12px] h-16  mb-6 animate-pulse mt-2" />;
const CategoryItemSkeleton = () => (
  <div className="rounded-[12px] w-1/3  relative flex flex-col items-center p-2 ">
    <Skeleton className="w-[100px] h-[100px] rounded-[12px]" />
    <Skeleton className="w-3/4 h-4 mt-2" />
  </div>
);
const CartSkeleton = () => (
  <div className="w-[100%] border border-[#e3e3e3] rounded-[12px] max-w-[500px] p-2 mt-6 bg-white flex flex-col items-center text-center shadow-sm animate-pulse">
    <Skeleton className="w-1/2 h-6 mt-2 mb-4" />
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="flex flex-row justify-between items-center w-full border-b p-2">
        <Skeleton className="w-1/4 h-4" />
        <div className="w-[60%] flex justify-between">
          <Skeleton className="w-1/6 h-4" />
          <Skeleton className="w-1/4 h-4" />
        </div>
      </div>
    ))}
    <Skeleton className="w-full h-10 mt-4 rounded-[12px]" />
  </div>
);

const TrustSectionSkeleton = () => (
  <div className="p-2 animate-pulse">
    <div className="bg-white rounded-[16px] p-4 shadow-md border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Skeleton className="w-6 h-6 rounded" />
          <Skeleton className="h-6 w-28" />
        </div>
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      {/* 2x2 Grid */}
      <div className="grid grid-cols-2 gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-gray-50 rounded-[12px] p-3 border border-gray-100">
            <div className="flex items-start justify-between mb-2">
              <Skeleton className="w-10 h-10 rounded-full" />
              <Skeleton className="w-10 h-5 rounded" />
            </div>
            <Skeleton className="h-4 w-3/4 mb-1" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const HowItWorksSectionSkeleton = () => (
  <div className="p-2 animate-pulse">
    <div className="bg-white rounded-[16px] p-4 shadow-md border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="mb-4">
        <Skeleton className="h-6 w-32 mb-1" />
        <Skeleton className="h-4 w-24" />
      </div>
      {/* Steps */}
      <div className="flex justify-between gap-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex flex-col items-center min-w-[60px] flex-1">
            <Skeleton className="w-12 h-12 rounded-full mb-2" />
            <Skeleton className="w-10 h-3 mb-1" />
            <Skeleton className="w-8 h-2" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ReferBannerSkeleton = () => <div className="bg-gray-200 rounded-xl p-2 mt-2 flex items-center shadow-sm animate-pulse h-10 w-full" />;
const LiveActivitySkeleton = () => <div className="rounded-xl p-1 flex items-center justify-center mt-2 mb-2 bg-gray-200 animate-pulse h-8 w-full" />;

const WhyChooseSortKaroSkeleton = () => <div className="h-48 bg-gray-200 animate-pulse rounded-xl my-2" />;
const InfiniteMotionTestimonialsSkeleton = () => <div className="h-48 bg-gray-200 animate-pulse rounded-xl my-2" />;
const HowSortkaroWorksSkeleton = () => <div className="h-48 bg-gray-200 animate-pulse rounded-xl my-2" />;
const ReviewsPreviewSkeleton = () => (
  <div className="p-2 animate-pulse">
    <div className="bg-white rounded-[16px] p-4 shadow-md border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Skeleton className="w-6 h-6 rounded" />
          <div>
            <Skeleton className="h-6 w-10 mb-1" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <div className="flex gap-1">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className={`h-2 rounded-full ${i === 0 ? 'w-5' : 'w-2'}`} />
          ))}
        </div>
      </div>
      {/* Review Card */}
      <div className="bg-gray-50 rounded-[12px] p-4 border border-gray-100">
        <div className="flex items-start gap-3 mb-3">
          <Skeleton className="w-11 h-11 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-4 w-28 mb-1" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
        <div className="flex gap-0.5 mb-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="w-4 h-4 rounded" />
          ))}
        </div>
        <Skeleton className="h-4 w-full mb-1" />
        <Skeleton className="h-4 w-3/4 mb-3" />
        <Skeleton className="h-5 w-40" />
      </div>
      {/* Bottom Badges */}
      <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Skeleton className="w-4 h-4 rounded" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const HourlyServiceSkeleton = () => (
  <div className="w-full max-w-[500px] mx-auto mb-4 animate-pulse">
    <Skeleton className="h-48 rounded-[12px]" />
  </div>
);

/* ================================ */
/*     CATEGORY IMAGE WITH SKELETON  */
/* ================================ */
const CategoryImage = memo(({ src, alt, onClick }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  return (
    <div 
      className="flex items-center justify-center rounded-lg w-20 h-20 cursor-pointer relative"
      onClick={onClick}
    >
      {isLoading && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Skeleton className="w-14 h-14 xs:w-16 xs:h-16 sm:w-20 sm:h-20 rounded-lg" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`w-14 h-14 xs:w-16 xs:h-16 sm:w-20 sm:h-20 object-contain transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        loading="lazy"
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
      />
    </div>
  );
});

/* ================================ */
/*          MEMO COMPONENTS          */
/* ================================ */
const TrustSection = memo(() => (
  <div className="p-2">
    <div className="bg-white rounded-[16px] p-4 shadow-md border border-gray-100">
      {/* Header with Rating */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-[#00489b]" />
          <h3 className="font-extrabold text-lg text-gray-900">Why Trust Us</h3>
        </div>
        <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-full">
          <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
          <span className="text-sm font-bold text-yellow-700">4.8 Rating</span>
        </div>
      </div>
      {/* 2x2 Grid Cards */}
      <div className="grid grid-cols-2 gap-3">
        {trustItems.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="bg-[#F8FAFC] rounded-[12px] p-3 border border-gray-100">
              <div className="flex items-start justify-between mb-2">
                <div className="bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-sm">
                  <Icon className="w-5 h-5 text-[#00489b]" />
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${item.badgeColor}`}>
                  {item.badge}
                </span>
              </div>
              <h4 className="font-bold text-sm text-gray-900 mb-0.5">{item.label}</h4>
              <p className="text-xs text-gray-500 leading-tight">{item.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </div>
));

const HowItWorksSection = memo(() => (
  <div className="p-2">
    <div className="bg-white rounded-[16px] p-4 shadow-md border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="mb-4">
        <h3 className="font-extrabold text-lg text-gray-900">How It Works</h3>
        <p className="text-sm text-gray-500">Simple & Quick</p>
      </div>
      {/* Steps - Horizontal Scroll */}
      <div className="flex justify-between gap-2 overflow-x-auto scrollbar-hide pb-2">
        {steps.map((item, index) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="flex flex-col items-center min-w-[60px] flex-1 relative">
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="absolute top-6 left-[60%] w-full h-[2px] bg-gray-200" />
              )}
              <div className="bg-[#E3F2FD] rounded-full w-12 h-12 flex items-center justify-center mb-2 shadow-sm z-10">
                <Icon className="w-5 h-5 text-[#00489b]" />
              </div>
              <span className="text-xs font-bold text-gray-900 text-center">{item.label}</span>
              <span className="text-[10px] text-gray-500 text-center">{item.sublabel}</span>
            </div>
          );
        })}
      </div>
    </div>
  </div>
));

const ReferBanner = memo(() => (
  <div className="bg-[#00897B] rounded-[16px] px-4 py-3 mt-2 flex items-center justify-center text-center shadow-sm">
    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
      <Gift size={22} className="text-[#00897B]" />
    </div>
    <span className="text-white font-bold text-base">Refer a friend & get ₹100 credit!</span>
  </div>
));

const ReviewsPreview = memo(() => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  // Auto-slide every 5 seconds with smooth loop
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= reviewsPreview.length - 1) {
          // Smooth loop back to first
          setIsTransitioning(false);
          setTimeout(() => {
            setIsTransitioning(true);
          }, 50);
          return 0;
        }
        return prev + 1;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="p-2">
      <div className="bg-white rounded-[16px] p-4 shadow-md border border-gray-100 overflow-hidden">
        {/* Header with Rating and Dots */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
            <div>
              <span className="text-2xl font-extrabold text-gray-900">4.8</span>
              <p className="text-xs text-gray-500">(350+ reviews)</p>
            </div>
          </div>
          {/* Pagination Dots */}
          <div className="flex gap-1">
            {reviewsPreview.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-2 rounded-full transition-all duration-500 ease-out ${idx === currentIndex
                  ? "w-5 bg-[#00489b]"
                  : "w-2 bg-gray-300 hover:bg-gray-400"
                  }`}
              />
            ))}
          </div>
        </div>

        {/* Sliding Review Card */}
        <div className="relative overflow-hidden">
          <div
            className={`flex ${isTransitioning ? 'transition-transform duration-700 ease-out' : ''}`}
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {reviewsPreview.map((review, idx) => (
              <div key={idx} className="w-full flex-shrink-0 px-0.5">
                <div className="bg-[#F8FAFC] rounded-[12px] p-4 border border-gray-100">
                  {/* User Info */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full bg-[#00489b] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {review.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-gray-900">{review.name}</span>
                        <BadgeCheck className="w-4 h-4 text-green-500" />
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{review.location}</span>
                        <span>•</span>
                        <span className="whitespace-nowrap">{review.daysAgo}</span>
                      </div>
                    </div>
                  </div>

                  {/* Star Rating */}
                  <div className="flex gap-0.5 mb-2">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-gray-700 text-sm italic mb-3 leading-relaxed line-clamp-2">
                    "{review.text}"
                  </p>

                  {/* Service Badge */}
                  <div className="flex items-center gap-1.5">
                    <BadgeCheck className="w-4 h-4 text-[#00489b]" />
                    <span className="text-[#00489b] font-semibold text-sm truncate">{review.service}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Trust Badges */}
        <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <BadgeCheck className="w-4 h-4 text-[#00489b]" />
            <span className="text-sm font-medium text-gray-700">Verified</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Shield className="w-4 h-4 text-[#00489b]" />
            <span className="text-sm font-medium text-gray-700">Trusted</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Headphones className="w-4 h-4 text-[#00489b]" />
            <span className="text-sm font-medium text-gray-700">24/7 Support</span>
          </div>
        </div>
      </div>
    </div>
  );
});

const LiveActivity = memo(() => (
  <div className="bg-white rounded-[16px] px-4 py-3 flex items-center mt-3 mb-4 border border-gray-200 shadow-sm">
    <Zap size={18} className="text-[#00489b] mr-2 fill-[#00489b]" />
    <span className="text-gray-800 font-semibold text-sm">
      14 people booked today in your area!
    </span>
  </div>
));

/* ================================ */
/*     HOURLY HOUSE HELP SECTION     */
/* ================================ */
const HourlyHouseHelpSection = memo(({ hourlyService, onOptionClick }) => {
  // console.log("Rendering HourlyHouseHelpSection with services:", hourlyService);
  if (!hourlyService || hourlyService.length === 0) return null;

  // const service = hourlyService[0];
  // const offering = service?.offerings?.[0];

  // if (!offering) return null;

  // const options = offering.options || [];
  // const selectedSubcategory = service?.subCategory?.name;
  // const selectedSubcategoryId = service?.subCategory?.id;

  return (
    <div className="w-full lg:max-w-[650px] md:max-w-[400px] mx-auto mb-8 p-4 bg-white border border-[#f0f0f0] rounded-[16px] shadow-[0_3px_8px_rgba(0,0,0,0.08)]">

      {/* Title + NEW badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-[#111] font-extrabold text-xl">
            Hourly House Help Services
          </h3>

          <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-md font-bold">
            NEW
          </span>
        </div>
      </div>

      {/* Description */}


      {/* Horizontal scrollable cards */}
      <div className="flex gap-4 overflow-x-auto overflow-y-hidden scrollbar-hide py-2">
        {hourlyService?.map((option) => {
          const discountedPrice = calculateDiscountedPrice(
            option.price,
            option.discount,
            option.discountUnit
          );
          const isFull = option?.status === "full" || option.isLive === false;
          const isAvailable = option?.status === "available" || option.isLive === true;
          const discountPercent = Math.round(((option.price - discountedPrice) / option.price) * 100);

          // Parse duration for display
          const getDurationDisplay = () => {
            const dur = option.duration;
            const unit = option.durationUnit?.toLowerCase();
            if (dur === 30 && unit === 'min') return '30 min';
            if (dur === 1 && (unit === 'hr' || unit === 'hour')) return '1 hr';
            if (dur === 1.5 || (dur === 90 && unit === 'min')) return '1.5 hrs';
            if (dur === 2 && (unit === 'hr' || unit === 'hour')) return '2 hrs';
            if (dur === 3 && (unit === 'hr' || unit === 'hour')) return '3 hrs';
            return `${dur} ${option.durationUnit}`;
          };

          return (
            <div
              key={option.id}
              className="min-w-[132px] max-w-[132px] flex-shrink-0 flex flex-col items-center"
            >
              {/* Card */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => onOptionClick?.(option)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOptionClick?.(option);
                  }
                }}
                className={`w-[132px] h-[150px] rounded-[12px] border relative overflow-hidden
                  ${isFull
                    ? "bg-gray-50 border-gray-200"
                    : "bg-white border-[#e3e3e3] shadow-[0_2px_6px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)]"
                  } transition-all duration-200 cursor-pointer`}
              >
                {/* Discount Badge / Fully Booked Badge */}
                {/* Discount/Fully Booked pill - top-left, small, no background */}
                <div className="absolute top-2 left-2 z-10">
                  {isFull ? (
                    <span className="text-[10px] font-bold text-gray-400 px-2 py-0.5 rounded-full border border-gray-200 bg-white/80" style={{ letterSpacing: '0.2px' }}>
                      FULLY BOOKED
                    </span>
                  ) : discountPercent > 0 ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-[#ffd699] text-[#b45309] bg-white/80" style={{ letterSpacing: '0.2px' }}>
                      {discountPercent}% OFF
                    </span>
                  ) : null}
                </div>

                {/* Duration & Price - Centered in card */}
                <div className={`absolute inset-0 flex flex-col items-center justify-center select-none ${isFull ? 'opacity-50' : ''}`}>
                  <span className={`text-xl font-extrabold select-none ${isFull ? 'text-gray-400' : 'text-[#1a1a1a]'}`}>
                    {getDurationDisplay()}
                  </span>
                  <div className="flex items-center gap-2 mt-1 select-none">
                    <span className={`text-base font-bold select-none ${isFull ? 'text-gray-400' : 'text-[#1a1a1a]'}`}>
                      ₹{discountedPrice}
                    </span>
                    {discountedPrice < option.price && (
                      <span className="text-xs text-gray-400 line-through">
                        ₹{option.price}
                      </span>
                    )}
                  </div>
                  {isAvailable && (
                    <div className="absolute bottom-1 right-1" onClick={(e) => e.stopPropagation()}>

                      <AddToCartButton
                        option={option}
                      
                        Discount={discountedPrice}
                       
                      />

                    </div>
                  )}

                </div>

              </div>

              {/* Add to Cart Button Below Card */}

              {/* Service Name Below Button */}
              <button
                type="button"
                onClick={() => onOptionClick?.(option)}
                className={`mt-2 text-xs font-semibold text-center select-none underline-offset-2 hover:underline ${isFull ? 'text-gray-400' : 'text-[#333]'}`}
              >
                {option?.name}
              </button>
            </div>
          );
        })}
      </div>

    </div>
  );
});


/* ================================ */
/*       SINGLE CATEGORY MODAL       */
/* ================================ */
const SingleCategoryModal = memo(({ show, onClose, option, singleSubcategory, normalCleaning = true }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartYRef = useRef(null);
  const sheetRef = useRef(null);

  const discountedPrice = useMemo(() => {
    return option ? calculateDiscountedPrice(option.price, option.discount, option.discountUnit) : 0;
  }, [option]);

  const isAvailable = option?.status === "available" || option?.isLive === true;
  const discountPercent = option && option.price > discountedPrice
    ? Math.round(((option.price - discountedPrice) / option.price) * 100)
    : 0;

  useEffect(() => {
    if (show) {
      setIsClosing(false);
      setDragY(0);
      setTimeout(() => setIsAnimating(true), 30);
    } else {
      setIsClosing(true);
      setIsAnimating(false);
    }
  }, [show]);

  useEffect(() => {
    const updateViewport = () => setIsMobileView(window.innerWidth < 768);
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    if (!show) return;
    const scrollY = window.scrollY;
    const previousBodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      overflowY: document.body.style.overflowY,
      overscrollBehaviorY: document.body.style.overscrollBehaviorY,
      width: document.body.style.width,
    };
    const previousHtmlOverscroll = document.documentElement.style.overscrollBehaviorY;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflowY = "scroll";
    document.body.style.overscrollBehaviorY = "contain";
    document.documentElement.style.overscrollBehaviorY = "contain";
    document.body.style.width = "100%";

    return () => {
      document.body.style.position = previousBodyStyle.position;
      document.body.style.top = previousBodyStyle.top;
      document.body.style.left = previousBodyStyle.left;
      document.body.style.right = previousBodyStyle.right;
      document.body.style.overflowY = previousBodyStyle.overflowY;
      document.body.style.overscrollBehaviorY = previousBodyStyle.overscrollBehaviorY;
      document.documentElement.style.overscrollBehaviorY = previousHtmlOverscroll;
      document.body.style.width = previousBodyStyle.width;
      window.scrollTo(0, scrollY);
    };
  }, [show]);

  const handleClose = () => {
    setDragY(0);
    setIsClosing(true);
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 520);
  };

  if (!option) return null;

  const handleDragStart = (e) => {
    if (!isMobileView) return;
    dragStartYRef.current = e.touches ? e.touches[0].clientY : e.clientY;
  };

  const handleDragMove = (e) => {
    if (!isMobileView || dragStartYRef.current == null) return;
    if (e.cancelable) e.preventDefault();
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = Math.max(0, currentY - dragStartYRef.current);
    setDragY(delta);
  };

  const handleDragEnd = () => {
    if (!isMobileView) return;
    const sheetHeight = sheetRef.current?.offsetHeight || 0;
    const closeThreshold = sheetHeight > 0 ? sheetHeight * 0.3 : 110;
    if (dragY > closeThreshold) {
      handleClose();
      return;
    }
    setDragY(0);
    dragStartYRef.current = null;
  };

  const mobileTranslate = isAnimating ? dragY : 420 + dragY;

  return (
    /* Backdrop */
    <div
      className={`fixed inset-0 ${isMobileView ? "z-[44]" : "z-[120]"} transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${show ? "opacity-100 visible" : "opacity-0 invisible"}`}
      style={{ backgroundColor: show ? "rgba(0,0,0,0.55)" : "transparent", backdropFilter: show ? "blur(2px)" : "none" }}
      onClick={handleClose}
    >
      {/* Sheet — slides up from bottom */}
      <div
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        className={`fixed inset-x-0 bottom-0 md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-6 md:w-[480px] bg-white md:rounded-[24px] rounded-t-[24px] shadow-2xl ${isMobileView ? "z-[45]" : "z-[121]"} flex flex-col h-[88vh] max-h-[88dvh] md:h-auto md:max-h-[92vh] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isAnimating ? "translate-y-0 opacity-100" : "translate-y-full md:translate-y-8 opacity-0"}`}
        style={isMobileView ? { transform: `translateY(${mobileTranslate}px)` } : undefined}
      >
        {/* Drag handle */}
        <div
          className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab active:cursor-grabbing touch-none"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleDragStart}
          onMouseMove={(e) => dragStartYRef.current != null && handleDragMove(e)}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
        >
          <div className="w-12 h-1.5 rounded-full bg-gray-300" />
        </div>

        {/* Banner image */}
        <div
          className="relative w-full h-[210px] sm:h-[240px] flex-shrink-0 overflow-hidden rounded-t-[16px] md:rounded-t-0 bg-gradient-to-b from-[#f6f9ff] to-[#eef4ff] cursor-grab active:cursor-grabbing touch-none"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleDragStart}
          onMouseMove={(e) => dragStartYRef.current != null && handleDragMove(e)}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
        >
          <Banner
            categoryName={option.name}
            imageUrl={option?.imageUrl}
            iconUrl={option?.iconUrl}
            normalCleaning={normalCleaning}
            className="w-full h-full object-contain p-3"
          />
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-gray-700" />
          </button>
          {/* Discount badge */}
          {discountPercent > 0 && (
            <div className="absolute top-3 left-3 bg-[#FF5722] text-white text-xs font-extrabold px-2.5 py-1 rounded-full shadow">
              {discountPercent}% OFF
            </div>
          )}
        </div>

        {/* Sticky header — name + price + add button */}
        <div
          className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 flex-shrink-0 bg-white cursor-grab active:cursor-grabbing touch-none"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleDragStart}
          onMouseMove={(e) => dragStartYRef.current != null && handleDragMove(e)}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
        >
          <div>
            <h2 className="text-[18px] font-extrabold text-gray-900 leading-tight">{option.name}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[20px] font-black text-[#00489b]">₹{discountedPrice}</span>
              {discountPercent > 0 && (
                <span className="text-sm text-gray-400 line-through">₹{option.price}</span>
              )}
              {!isAvailable && (
                <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">Unavailable</span>
              )}
            </div>
          </div>
          {isAvailable && (
            <div className="flex-shrink-0">
              <AddToCartBannerButton option={option} Discount={discountedPrice} />
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 scrollbar-hide px-4 pt-4 pb-6 space-y-5">

          {/* What's Included */}
          {option.includes?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-[#00489b] rounded-full" />
                <h3 className="font-extrabold text-[15px] text-gray-900">What's Included</h3>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {option.includes.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-[10px] px-3 py-2">
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-gray-800 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Not Included */}
          {option.doesNotInclude?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-red-400 rounded-full" />
                <h3 className="font-extrabold text-[15px] text-gray-900">Not Included</h3>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {option.doesNotInclude.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-[10px] px-3 py-2">
                    <div className="w-5 h-5 rounded-full bg-red-400 flex items-center justify-center flex-shrink-0">
                      <XCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* How It Works */}
          {option.howItWorks?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-purple-400 rounded-full" />
                <h3 className="font-extrabold text-[15px] text-gray-900">How It Works</h3>
              </div>
              <div className="space-y-2">
                {option.howItWorks.map((step, i) => (
                  <div key={step.id || i} className="flex gap-3 bg-purple-50 border border-purple-100 rounded-[12px] p-3">
                    <div className="w-7 h-7 rounded-full bg-[#00489b] text-white text-xs font-extrabold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <p className="font-bold text-sm text-gray-900">{step.title}</p>
                      {step.description && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{step.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* After Care Tips */}
          {option.afterCareTips?.length > 0 && (
            <div className="rounded-[14px] bg-[#FFF8E7] border border-[#FFE082] p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-[#FFC107] flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-extrabold text-[15px] text-[#7B5800]">After Care Tips</h3>
              </div>
              <ul className="space-y-2.5">
                {option.afterCareTips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#FFC107] text-white text-[10px] font-extrabold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-sm text-[#5C4000] leading-snug">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* FAQs */}
          {option.faqs?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-[#00489b] rounded-full" />
                <h3 className="font-extrabold text-[15px] text-gray-900">FAQs</h3>
              </div>
              <Accordion type="single" collapsible className="w-full space-y-2">
                {option.faqs.map((faq) => (
                  <AccordionItem
                    key={faq.id}
                    value={faq.id}
                    className="border border-gray-200 rounded-[12px] px-3 overflow-hidden bg-[#F8FAFC]"
                  >
                    <AccordionTrigger className="text-sm font-bold text-gray-900 py-3 hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-gray-600 pb-3 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

        </div>

      </div>
    </div>
  );
});

/* ================================ */
/*           MAIN COMPONENT          */
/* ================================ */
export default function Category() {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("normal");
  const [selectedOption, setSelectedOption] = useState(null);
  const [isNormalCleaning, setIsNormalCleaning] = useState(true);
  const [showMobileCartSheet, setShowMobileCartSheet] = useState(false);
  const [isCartSheetVisible, setIsCartSheetVisible] = useState(false);
  const [cartSheetDragY, setCartSheetDragY] = useState(0);
  const cartSheetDragStartYRef = useRef(null);

  const dispatch = useDispatch();
  const router = useRouter();

  const { data, offers, single, loading, loadingSegmnts, error, serviceOfDay } = useSelector((state) => state.category);
  const singleSubcategory = useSelector((state) => state.category.singleSubcategory);
  const deepCleaning = useSelector((state) => state.category.deepCleaning);
  const carwash = useSelector((state) => state.category.carwash);
  const cart = useSelector((state) => state.cart.items);
  const hourlyService = useSelector((state) => state.category.hourlyService);
  // console.log("Category data:", { singleSubcategory, deepCleaning, hourlyService, loadingSegmnts });

  const normalId = "6893bf367affa517ae74622e";
  const deepCleaningId = "68d00f022b247c894e097334";
  const hourlyServiceId = "691380a00db0c695933359c2";
  const carWashId = "695127c56183535423530a70";
  //691380a00db0c695933359c2
  //  https://prod.content.ideayatra.com/api/v1/segments/subcategory/691380a00db0c695933359c2

  // Ref to prevent duplicate fetches in Strict Mode
  const hasFetchedRef = useRef(false);
  const carPackagesRef = useRef(null);

  useEffect(() => {
    // Skip if already fetched
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    (async () => {
      try {
        await Promise.all([
          // dispatch(fetchServiceOfDay()),
          dispatch(fetchCategoriesOption({ id: normalId, type: "normal" })).unwrap(),
          dispatch(fetchCategoriesOption({ id: deepCleaningId, type: "deep" })).unwrap(),
          dispatch(fetchCategoriesOption({ id: hourlyServiceId, type: "hourly" })).unwrap(),
          dispatch(fetchCategoriesOption({ id: carWashId, type: "carwash" })).unwrap(),
        ]);
      } catch (err) {
        // Ignore ConditionError (expected when data already exists)
        if (err?.name !== 'ConditionError') {
          console.error(err);
        }
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    if (cart?.length) localStorage.setItem("cart", JSON.stringify(cart));
    else localStorage.removeItem("cart");
  }, [cart]);

  useEffect(() => {
    const openCartSheet = () => setShowMobileCartSheet(true);
    window.addEventListener("open-home-cart-sheet", openCartSheet);

    return () => window.removeEventListener("open-home-cart-sheet", openCartSheet);
  }, []);

  useEffect(() => {
    if (!showMobileCartSheet) return;
    setCartSheetDragY(0);
    setIsCartSheetVisible(false);
    requestAnimationFrame(() => setIsCartSheetVisible(true));
  }, [showMobileCartSheet]);

  const closeMobileCartSheet = useCallback(() => {
    setIsCartSheetVisible(false);
    setCartSheetDragY(0);
    setTimeout(() => setShowMobileCartSheet(false), 420);
  }, []);

  const handleCartSheetDragStart = useCallback((e) => {
    cartSheetDragStartYRef.current = e.touches ? e.touches[0].clientY : e.clientY;
  }, []);

  const handleCartSheetDragMove = useCallback((e) => {
    if (cartSheetDragStartYRef.current == null) return;
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = Math.max(0, currentY - cartSheetDragStartYRef.current);
    setCartSheetDragY(delta);
  }, []);

  const handleCartSheetDragEnd = useCallback(() => {
    if (cartSheetDragY > 100) {
      closeMobileCartSheet();
      return;
    }
    setCartSheetDragY(0);
    cartSheetDragStartYRef.current = null;
  }, [cartSheetDragY, closeMobileCartSheet]);



  const handleClick = useCallback(
    (option) => {
      setSelectedOption(option);
      setShowModal(true);
      // If option name includes "deep" (case insensitive), it's deep cleaning, otherwise normal
      const isDeepCleaning = option?.name?.toLowerCase().includes("deep");
      setIsNormalCleaning(!isDeepCleaning);
    },
    []
  );

  const handleHourlyServiceClick = useCallback((option, offering) => {
    setSelectedOption(option);
    setShowModal(true);
    setIsNormalCleaning(false);
  }, []);

  const closeModal = useCallback(() => setShowModal(false), []);

  const handleTabChange = useCallback((tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
  }, [activeTab]);

  const handleCarWashExplore = useCallback(() => {
    if (carPackagesRef.current) {
      carPackagesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setActiveTab("normal");
  }, []);

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + (item.Discount ?? (item.price - (item.discount || 0))) * (item.unit || 1), 0),
    [cart]
  );



  /* -------------------- */
  /* RENDERING CATEGORY GRID */
  const renderCategoryGrid = (categories, normalCleaning) =>
    categories?.map((op) => {
          const d = calculateDiscountedPrice(op.price, op.discount, op.discountUnit);
          const isFull = op?.status === "full" || op.isLive === false;

          const isAvailable = op?.status === "available" || op.isLive === true;

          return (
            <div key={op.id} className="rounded-[12px] w-1/3  relative flex flex-col items-center cursor-pointer select-none">
              <div  className={`flex flex-col  border border-[#f0f0f0] md:w-[104px] md:h-[104px] w-[98px] h-[98px] rounded-[12px] items-center justify-center relative shadow-[0_2px_4px_rgba(0,0,0,0.10)] transition-all duration-200 cursor-pointer ${isFull ? 'bg-gray-100' : 'bg-white'}`}>
                {/* Professional Creative FULL Badge - Small & Eye-Catching */}
                {isFull && <FullBadge position="top-right" />}
                {op.iconUrl || op.imageUrl ? (
                  <CategoryImage 
                    src={op.iconUrl || op.imageUrl}
                    alt={op.name}
                    onClick={() => handleClick(op)}
                  />
                ) : (
                  <CategoryIcon categoryName={op.name} size="xl" onClick={() => handleClick(op)} normalCleaning={normalCleaning} />
                )}
                
                {!isFull && (
                  <div className="absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1">
                    {
                      isAvailable && (
                        <AddToCartButton
                          option={op}
                          a
                          Discount={d}
                        
                        />
                      )
                    }

                  </div>
                )}
              </div>
              <div onClick={() => handleClick(op)} className="flex flex-col items-center justify-center text-center z-25 p-2 mt-1 cursor-pointer select-none">
                <span className={`text-sm md:text-md font-bold not-italic z-25 select-none ${isFull ? 'text-black ' : 'text-black'}`}>{op?.name}</span>

              </div>
            </div>
          );
        })
   


  return (
    <div className="min-h-screen w-full max-w-[1320px] mx-auto mt-[60px] md:mt-[85px] pb-2">
      {/* Service of the Day */}


      <div className="flex gap-6 lg:gap-12 px-3 md:px-6 lg:px-8 justify-center items-start">
        {/* Left Panel */}
        <div className="flex flex-col lg:pr-2 w-full lg:max-w-[780px] mt-4 mb-6">
          {/* <div className="w-full max-w-[1200px] mx-auto  z-20 bg-white rounded-[12px]">
            {loading ? <ServiceOfDayBarSkeleton /> : <DeepCleaning onBookNow={handleCarWashExplore} />}
          </div> */}

          {!loadingSegmnts && carwash?.length > 0 && (
            <div ref={carPackagesRef} className="w-full max-w-[660px] lg:max-w-[660px] mx-auto mb-4 p-3 sm:p-4 bg-white border border-[#f0f0f0] rounded-[16px] shadow-[0_3px_8px_rgba(0,0,0,0.08)]">
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#00489b] rounded-[10px] flex items-center justify-center flex-shrink-0">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 17H3V12L5.5 6H18.5L21 12V17H19M5 17H19M5 17C5 18.1 5.9 19 7 19C8.1 19 9 18.1 9 17M19 17C19 18.1 18.1 19 17 19C15.9 19 15 18.1 15 17" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="17" cy="17" r="1.5" fill="white"/>
                    <circle cx="7" cy="17" r="1.5" fill="white"/>
                    <path d="M18 9H6L5 12H19L18 9Z" fill="white" opacity="0.4"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[#111] font-extrabold text-base sm:text-xl leading-snug">Car & Bike Cleaning</h3>
                  <p className="text-xs sm:text-sm text-gray-500">Wash & cleaning at your doorstep</p>
                </div>
                <span className="bg-green-100 text-green-700 text-[10px] sm:text-xs px-2 py-0.5 sm:py-1 rounded-md font-bold flex-shrink-0">NEW</span>
              </div>

              {/* Horizontally scrollable pill cards */}
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                {carwash.map((option) => {
                  const discountedPrice = calculateDiscountedPrice(option.price, option.discount, option.discountUnit);
                  const isFull = option?.status === "full" || option?.isLive === false;
                  const cartItem = cart.find((item) => item.id === option.id);
                  const inCart = cartItem && cartItem.quantity > 0;

                  return inCart ? (
                    /* ── SELECTED STATE: blue pill with — qty + ── */
                    <div
                      key={option.id}
                      onClick={() => handleClick(option)}
                      className="flex items-center flex-shrink-0 bg-[#00489b] rounded-full px-3 py-2 sm:px-4 sm:py-2.5 select-none gap-2"
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(decreaseQuantity(option.id));
                        }}
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <div className="flex flex-col items-center leading-tight">
                        <span className="text-[13px] font-extrabold text-white whitespace-nowrap">{option.name}</span>
                        <span className="text-[11px] font-bold text-blue-200 whitespace-nowrap">{cartItem.quantity}</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const maxQty = getMaxQtyForOption(option.name);
                          if ((cartItem?.quantity || 0) >= maxQty) {
                            toast.error(getMaxQtyMessage(option.name));
                            return;
                          }
                          dispatch(increaseQuantity(option.id));
                        }}
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    /* ── DEFAULT STATE: white pill with name, price, + button ── */
                    <div
                      key={option.id}
                      onClick={() => !isFull && handleClick(option)}
                      className={`flex items-center gap-2 sm:gap-3 flex-shrink-0 bg-white border border-[#e0e0e0] rounded-full px-3 py-2 sm:px-4 sm:py-2.5 cursor-pointer select-none ${isFull ? "opacity-50 cursor-default" : ""}`}
                    >
                      <div className="flex flex-col leading-tight">
                        <span className="text-[12px] sm:text-sm font-bold text-[#111] whitespace-nowrap">{option.name}</span>
                        <span className="text-[12px] sm:text-sm font-bold text-[#00489b] whitespace-nowrap">₹{discountedPrice}</span>
                      </div>
                      {!isFull ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(addToCart({ ...option, Discount: discountedPrice }));
                          }}
                          className="w-8 h-8 rounded-full border-2 border-[#00489b] bg-white text-[#00489b] flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">Full</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Hourly House Help Section */}

          {loadingSegmnts ? (
            <HourlyServiceSkeleton />
          ) : (
             (
              <HourlyHouseHelpSection
                hourlyService={hourlyService}
                onOptionClick={handleHourlyServiceClick}
              />
            )
          )}


          <div className="flex flex-col w-full justify-evenly md:gap-4 px-2 md:px-0">
            {/* Tabs */}
            <div className="md:flex items-center justify-center w-full max-w-[760px] lg:max-w-[820px]">
              <div className="flex w-full md:w-[620px] lg:w-[620px] items-center bg-white border border-[#e3e3e3] rounded-[12px] p-1 relative">
                {/* Sliding background indicator */}
                <div
                  className="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-[#00489b] rounded-[12px] shadow transition-transform duration-500 ease-out"
                  style={{
                    transform: activeTab === "normal" ? "translateX(4px)" : "translateX(calc(100% + 4px))"
                  }}
                />

                {["normal", "deep"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    aria-pressed={activeTab === tab}
                    className={`px-4 w-1/2 py-3 rounded-[12px] font-semibold transition-all duration-500 relative z-10 ${activeTab === tab ? "text-white" : "text-gray-700 hover:text-gray-900"
                      }`}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <div className="text-left">
                        <div className="text-sm md:text-md font-semibold">
                          {tab === "normal" ? "Normal Cleaning" : "Deep Cleaning"}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Grids - Simple show/hide without slide animation */}
            <div className="w-full flex justify-center lg:justify-center">
              {/* Normal Cleaning Grid */}
              <div
                className={`${activeTab === "normal" ? "block" : "hidden"} w-full flex justify-center lg:justify-center`}
                data-section="normal-cleaning"
              >
                <div className="md:mt-2 w-full max-w-[600px] lg:max-w-[500px] p-2 sm:border border-[#f0f0f0] rounded-[12px] mt-2 shadow-[0_2px_4px_rgba(0,0,0,0.10)] flex flex-wrap justify-center py-2 md:p-2 md:py-5">
                  {loadingSegmnts
                    ? Array.from({ length: 12 }).map((_, i) => <CategoryItemSkeleton key={i} />)
                    : renderCategoryGrid(singleSubcategory, true)}
                </div>
              </div>

              {/* Deep Cleaning Grid */}
              <div
                className={`${activeTab === "deep" ? "block" : "hidden"} w-full flex justify-center lg:justify-center`}
                data-section="deep-cleaning"
              >
                <div className="md:mt-2 w-full max-w-[660px] lg:max-w-[500px] p-2 sm:border border-[#f0f0f0] rounded-[12px] mt-2 shadow-[0_2px_4px_rgba(0,0,0,0.10)] flex flex-wrap justify-center py-2 md:p-2 md:py-5">
                  {loadingSegmnts
                    ? Array.from({ length: 12 }).map((_, i) => <CategoryItemSkeleton key={i} />)
                    : renderCategoryGrid(deepCleaning, false)}
                </div>
              </div>
            </div>

            {/* Tablet Cart Block (md-lg) */}
            <div className="hidden md:block xl:hidden w-full max-w-[760px] lg:max-w-[820px] mx-auto mt-4">
              {cart.length > 0 ? (
                <div className="border border-[#e3e3e3] rounded-[12px] bg-white shadow-sm">
                  <div className="px-4 py-3 flex items-center justify-between border-b border-[#edf1f6]">
                    <h3 className="text-[20px] font-bold text-[#00489b]">Your Cart</h3>
                    <span className="bg-[#00489b] text-white text-xs px-2 py-1 rounded-full">
                      {cart.reduce((total, item) => total + (item.unit || 0), 0)} items
                    </span>
                  </div>
                  <div className="max-h-[46vh] overflow-y-auto px-3 py-2 space-y-2">
                    {cart.map((item) => {
                      const d = calculateDiscountedPrice(item.price, item.discount, item.discountUnit);
                      const unitPrice = item.price - (item.discount || 0);
                      const lineTotal = unitPrice * item.unit;
                      return (
                        <div key={item.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center border border-[#edf1f6] rounded-[10px] p-3 gap-3 bg-white">
                          <div className="min-w-0">
                            <h5 className="font-bold text-[14px] text-[#151515] line-clamp-2">{item.name}</h5>
                            <p className="text-[12px] text-gray-500 mt-1">₹{unitPrice} x {item.unit}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="font-bold text-[#00489b] text-[15px]">₹{lineTotal}</p>
                              {item.discount > 0 && <p className="font-semibold text-[12px] text-gray-400 line-through">₹{item.price * item.unit}</p>}
                            </div>
                            <AddToCartButton option={item} showUnitLabel={false} Discount={d} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="p-3 border-t bg-white">
                    <button
                      onClick={() => router.push("/cart/6893bf367affa517ae74622e")}
                      className="w-full h-10 bg-[#00489b] text-white rounded-[12px] font-semibold hover:bg-[#00489b] transition"
                    >
                      Proceed to Checkout
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border border-[#e3e3e3] rounded-[12px] p-5 bg-white text-center shadow-sm">
                  <h3 className="text-[18px] font-semibold text-gray-800">Your Cart is Empty</h3>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Right Panel - Cart */}
        <div className="hidden xl:block w-[390px] 2xl:w-[420px] flex-shrink-0">
          <div className="sticky top-16">
            {loading ? (
              <CartSkeleton />
            ) : cart.length > 0 ? (
              <div className="flex justify-center w-full">
                <div className="w-[100%] border border-[#e3e3e3] rounded-[12px] max-w-[520px] mt-4 bg-white flex flex-col shadow-sm h-[calc(100vh-210px)]">
                  <div className="w-full text-left mt-1 px-4 py-3 flex items-center justify-between">
                    <h2 className="text-[20px] font-bold text-[#00489b]">Your Cart</h2>
                    <span className="bg-[#00489b] text-white text-xs px-2 py-1 rounded-full">
                      {cart.reduce((total, item) => total + (item.unit || 0), 0)} items
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto scrollbar-hide px-3 pb-3">
                    {cart.map((item) => {
                      const d = calculateDiscountedPrice(item.price, item.discount, item.discountUnit);
                      const unitPrice = item.price - (item.discount || 0);
                      const lineTotal = unitPrice * item.unit;
                      return (
                        <div key={item.id} className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-center w-full border border-[#edf1f6] rounded-[10px] p-3 gap-3 mb-2 bg-white">
                          <div className="w-11 h-11 rounded-[8px] overflow-hidden bg-[#f3f6fb] border border-[#ebeff5] flex-shrink-0">
                            {item.iconUrl || item.imageUrl ? (
                              <img src={item.iconUrl || item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[#00489b] font-bold text-xs">
                                {item?.name?.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col text-left min-w-0 pr-1">
                            <h5 className="font-bold not-italic text-[14px] leading-tight text-[#151515] line-clamp-2">{item.name}</h5>
                            <p className="text-[12px] text-gray-500 mt-1">₹{unitPrice} x {item.unit}</p>
                          </div>
                          <div className="flex items-center gap-3 pl-1">
                            <div className="flex flex-col items-end text-right leading-tight">
                              <p className="font-bold text-[#00489b] text-[15px]">₹{lineTotal}</p>
                              {item.discount > 0 && <p className="font-semibold text-[12px] text-gray-400 line-through">₹{item.price * item.unit}</p>}
                            </div>
                            <div className="flex-shrink-0">
                              <AddToCartButton
                                option={item}
                                showUnitLabel={true}
                                Discount={d}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="p-2 border-t bg-white">
                    <button onClick={() => router.push("/cart/6893bf367affa517ae74622e")} className="w-full h-10 bg-[#00489b] text-white rounded-[12px] font-semibold hover:bg-[#00489b] transition">
                      Proceed to Checkout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-center w-full">
                <div className="w-[100%] border border-[#e3e3e3] rounded-[12px] max-w-[500px] p-6 mt-6 bg-white flex flex-col items-center text-center shadow-sm">
                  <div className="w-20 h-20 flex items-center justify-center bg-white rounded-full mb-4">
                    <ShoppingCart className="text-[#00489b] h-14 w-14" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">Your Cart is Empty</h2>
                  <p className="text-gray-500 text-sm mb-6">House Help in 15 in Min</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Components */}
      <div className="block md:hidden p-3 md:px-0">
        {/* <MobileUpcomingBookingsCard /> */}
        {loading ? <TrustSectionSkeleton /> : <TrustSection />}
        {loading ? <ReviewsPreviewSkeleton /> : <ReviewsPreview />}
        {loading ? <HowItWorksSectionSkeleton /> : <HowItWorksSection />}
        {loading ? <ReferBannerSkeleton /> : <ReferBanner />}
        {loading ? <LiveActivitySkeleton /> : <LiveActivity />}
      </div>

      {/* Desktop Components */}
      <div className="hidden md:block p-3 md:px-0">
        {loading ? <WhyChooseSortKaroSkeleton /> : <WhyChooseSortKaro />}
        {loading ? <InfiniteMotionTestimonialsSkeleton /> : <InfiniteMotionTestimonials />}
        {loading ? <HowSortkaroWorksSkeleton /> : <HowSortkaroWorks />}
      </div>

      {/* <UpcomingBookingsOverlay /> */}

      {/* Modal */}
      <SingleCategoryModal
        show={showModal}
        onClose={closeModal}
        singleSubcategory={isNormalCleaning ? singleSubcategory : deepCleaning}
        option={selectedOption}
        normalCleaning={isNormalCleaning}
      />

      {showMobileCartSheet && (
        <div className="fixed inset-0 z-[70] md:hidden">
          <button
            type="button"
            aria-label="Close cart overlay"
            onClick={closeMobileCartSheet}
            className="absolute inset-0 bg-black/40"
          />
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-[20px] bg-white p-4 max-h-[78vh] overflow-y-auto shadow-2xl transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateY(${(isCartSheetVisible ? 0 : 420) + cartSheetDragY}px)` }}
          >
            <div
              className="flex justify-center -mt-1 mb-2 cursor-grab active:cursor-grabbing"
              onTouchStart={handleCartSheetDragStart}
              onTouchMove={handleCartSheetDragMove}
              onTouchEnd={handleCartSheetDragEnd}
              onMouseDown={handleCartSheetDragStart}
              onMouseMove={(e) => cartSheetDragStartYRef.current != null && handleCartSheetDragMove(e)}
              onMouseUp={handleCartSheetDragEnd}
              onMouseLeave={handleCartSheetDragEnd}
            >
              <div className="w-12 h-1.5 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-[#00489b]">Your Cart</h3>
              <button
                type="button"
                onClick={closeMobileCartSheet}
                className="w-8 h-8 rounded-full border border-[#d8d8d8] text-gray-500"
              >
                <X className="w-4 h-4 mx-auto" />
              </button>
            </div>

            {cart.length === 0 ? (
              <p className="text-sm text-gray-500">Your cart is empty.</p>
            ) : (
              <div className="space-y-2">
                {cart.map((item) => {
                  const discounted = item.Discount ?? (item.price - (item.discount || 0));
                  return (
                    <div key={item.id} className="flex items-center justify-between gap-2 border border-[#ececec] rounded-[10px] p-2 bg-white">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-10 h-10 rounded-[8px] overflow-hidden bg-[#f3f6fb] border border-[#ebeff5] flex-shrink-0">
                          {item.iconUrl || item.imageUrl ? (
                            <img src={item.iconUrl || item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#00489b] font-bold text-xs">
                              {item?.name?.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-[#101010] truncate">{item.name}</p>
                        </div>
                        <p className="text-xs text-gray-500">₹{discounted} x {item.unit || 1}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#00489b]">₹{discounted * (item.unit || 1)}</span>
                        <AddToCartButton option={item} Discount={discounted} showUnitLabel={false} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-4 border-t pt-3 flex items-center justify-between">
              <p className="font-semibold text-[#101010]">Total: ₹{cartTotal}</p>
              <button
                type="button"
                onClick={() => {
                  closeMobileCartSheet();
                  router.push("/cart/6893bf367affa517ae74622e");
                }}
                className="bg-[#00489b] text-white font-semibold px-4 py-2 rounded-[10px]"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
